import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { TableSkeleton } from "../components/Skeletons";
import { Plus, MagnifyingGlass, Buildings, X, ListBullets, MapTrifold } from "@phosphor-icons/react";
import { toast } from "sonner";
import ClientPropertyDialog from "../components/ClientPropertyDialog";
import ClientPropertyBox from "../components/properties/ClientPropertyBox";
import PropertiesMap, { clientColours } from "../components/properties/PropertiesMap";

const normPc = (s) => (s || "").replace(/\s+/g, "").toUpperCase();
const matches = (p, q) => {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = ["name", "address_line", "town_city", "county", "postcode", "what3words", "site_contact_name"].map((k) => p[k] || "").join(" ").toLowerCase();
  const qpc = normPc(q);
  return words.every((w) => hay.includes(w)) || (qpc.length >= 2 && normPc(p.postcode).includes(qpc));
};

export default function PropertiesPage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("client") || "";
  const q = params.get("q") || "";
  const view = params.get("view") === "map" ? "map" : "list";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {clientId, property?}
  const [locating, setLocating] = useState(false);
  const locatedFor = useRef(""); // scope key already geocoded this visit

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await API.get(`/properties${selected ? `?client_id=${encodeURIComponent(selected)}` : ""}`);
      setData(d);
    } catch (e) {
      if (e.response?.status === 404) { setParam("client", ""); return; }
      toast.error("Failed to load properties");
    } finally { setLoading(false); }
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const tree = useMemo(() => data?.clients || [], [data]);
  const colours = useMemo(() => clientColours(tree), [tree]);
  const searching = q.trim().length > 0;
  const grouped = useMemo(() => {
    const g = {};
    (data?.properties || []).filter((p) => !searching || matches(p, q)).forEach((p) => (g[p.client_id] = g[p.client_id] || []).push(p));
    return g;
  }, [data, q, searching]);
  const matchedCount = Object.values(grouped).reduce((n, l) => n + l.length, 0);
  const visibleProps = useMemo(() => Object.values(grouped).flat(), [grouped]);
  const unplaced = (data?.properties || []).filter((p) => p.latitude == null || p.longitude == null);

  // Map view: pin anything without coordinates (geocoded server-side, a batch at a time), then refresh the list.
  const locate = useCallback(async (force = false) => {
    const key = selected || "__all__";
    if (locating || (!force && locatedFor.current === key)) return;
    locatedFor.current = key;
    setLocating(true);
    try {
      let placed = 0, r = null;
      for (let i = 0; i < 6; i++) {
        ({ data: r } = await API.post(`/properties/geocode${selected ? `?client_id=${encodeURIComponent(selected)}` : ""}`));
        placed += r.geocoded || 0;
        if (!r.configured || !r.geocoded || !r.remaining) break;
      }
      if (r && !r.configured) toast.error("Map lookups aren't configured on this server");
      else if (placed) toast.success(`${placed} propert${placed === 1 ? "y" : "ies"} placed on the map`);
      if (placed) await load();
    } catch { toast.error("Could not place properties on the map"); }
    finally { setLocating(false); }
  }, [selected, locating, load]);
  useEffect(() => {
    if (view === "map" && data && unplaced.length > 0) locate();
  }, [view, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const subOf = useMemo(() => {
    for (const c of tree) { const s = c.sub_clients.find((x) => x.id === selected); if (s) return { ...s, kind: "sub", parent_name: c.name, parent_id: c.id, sub_clients: [] }; }
    return null;
  }, [tree, selected]);
  const boxes = selected
    ? (subOf ? [subOf] : tree.filter((c) => c.id === selected))
    : tree.filter((c) => !searching || (grouped[c.id] || []).length > 0 || c.sub_clients.some((s) => (grouped[s.id] || []).length > 0));

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from ${p.client_name}'s portfolio? Existing jobs keep their address.`)) return;
    try { await API.delete(`/clients/${p.client_id}/properties/${p.id}`); toast.success("Property removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove property"); }
  };

  return (
    <div data-testid="properties-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Properties</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="properties-total">
            {data ? `${data.total_all} propert${data.total_all === 1 ? "y" : "ies"} across ${tree.length} client${tree.length === 1 ? "" : "s"}` : "Loading…"}
            {data && searching ? ` · ${matchedCount} match “${q}”` : ""}
          </p>
        </div>
        <Button onClick={() => setDialog({ clientId: selected })} disabled={!selected} title={selected ? "Add a property to the selected client" : "Choose a client or sub-client first, or use the Add button on a client box"} data-testid="properties-add-btn">
          <Plus size={16} className="mr-1" /> Add Property
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select value={selected} onChange={(e) => setParam("client", e.target.value)} className="h-9 min-w-[260px] max-w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="properties-client-select">
          <option value="">All clients & sub-clients</option>
          {tree.map((c) => c.kind === "main" ? (
            <optgroup key={c.id} label={`${c.name} (main client)`}>
              <option value={c.id}>{c.name} — main client & sub-clients ({c.property_count + c.sub_clients.reduce((n, s) => n + s.property_count, 0)})</option>
              {c.sub_clients.map((s) => <option key={s.id} value={s.id}>{"\u00A0\u00A0↳ "}{s.name} ({s.property_count})</option>)}
            </optgroup>
          ) : <option key={c.id} value={c.id}>{c.name} ({c.property_count})</option>)}
        </select>
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setParam("q", e.target.value)} placeholder="Search by address, postcode or property name…" className="pl-9 pr-8" data-testid="properties-search-input" />
          {q && <button type="button" onClick={() => setParam("q", "")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear search" data-testid="properties-search-clear"><X size={14} /></button>}
        </div>
        {selected && <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setParam("client", "")} data-testid="properties-clear-client">Show all clients</Button>}
        <div className="ml-auto flex items-center gap-1 border border-border rounded-md p-0.5 bg-muted/20" data-testid="properties-view-toggle">
          <button type="button" onClick={() => setParam("view", "")} className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded ${view === "list" ? "bg-background shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`} data-testid="properties-view-list"><ListBullets size={13} /> List</button>
          <button type="button" onClick={() => setParam("view", "map")} className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded ${view === "map" ? "bg-background shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`} data-testid="properties-view-map"><MapTrifold size={13} /> Map</button>
        </div>
      </div>

      {loading && !data ? <TableSkeleton rows={4} cols={3} /> : tree.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="properties-empty">
          <Buildings size={40} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">No clients yet — properties sit under a client or sub-client.</p>
          <Button variant="outline" size="sm" className="mt-3" asChild><Link to="/clients?new=1">Add your first client</Link></Button>
        </div>
      ) : view === "map" ? (
        <PropertiesMap properties={visibleProps} tree={tree} unplaced={unplaced} locating={locating}
          onLocate={() => locate(true)} onEdit={(p) => setDialog({ clientId: p.client_id, property: p })} onPickClient={(id) => setParam("client", id)} />
      ) : boxes.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="properties-no-match">
          <MagnifyingGlass size={40} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">No properties match “{q}”{selected ? " for this client" : ""}.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="properties-boxes">
          {boxes.map((c) => (
            <ClientPropertyBox key={c.id} client={c} ownProperties={grouped[c.id] || []} subProperties={grouped} searching={searching} colours={colours}
              onAdd={(clientId) => setDialog({ clientId })} onEdit={(p) => setDialog({ clientId: p.client_id, property: p })} onRemove={remove} />
          ))}
        </div>
      )}

      {dialog && <ClientPropertyDialog clientId={dialog.clientId} property={dialog.property} open onClose={() => setDialog(null)} onSaved={load} />}
    </div>
  );
}
